import React, {
  useState,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Box, Grid, Alert } from "@mui/material";
import {
  DataTable,
  ColumnConfig,
  RendererType,
} from "../components/DataTable";
import { useSorting } from "../hooks/useSorting";
import { useFilterParser } from "../hooks/useFilterParser";

interface ByDateProps {
  onLastUpdatedChange?: (lastUpdated: string) => void;
  filterState: any;
  onFilterChange: any;
}

export interface ByDateRef {
  refresh: () => void;
  getLastUpdated: () => string;
}

const ByDate = forwardRef<ByDateRef, ByDateProps>(
  ({ onLastUpdatedChange, filterState, onFilterChange }, ref) => {
    const [edFeed, setEdFeed] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [totalCount, setTotalCount] = useState(0);
    const [lastUpdated, setLastUpdated] = useState<string>("N/A");

    // Use custom hooks
    const { sortColumn, sortDirection, handleSort } = useSorting();
    const { toCommaSeparated } = useFilterParser();

    // Fetch data
    const fetchData = useCallback(
      async (isRefresh = false, signal?: AbortSignal) => {
        const {
          startDate,
          endDate,
          filter,
          quotedJobcode,
          techLdap,
          acctgDt,
          declineReason,
          applianceType,
          createSource,
          llmChatClassification,
          orderNumber,
          page,
          limit,
        } = filterState;

        try {
          setError(null);
          // Never clear data to maintain table height and smooth transitions
          // Data will be replaced when new data arrives

          if (isRefresh) {
            setRefreshing(true);
          } else {
            setLoading(true);
          }

          const feedResponse = await fetch(
            `api/snowflake/audit-data-ed-by-date`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                filter: toCommaSeparated(filter),
                quotedJobcode: toCommaSeparated(quotedJobcode),
                techLdap: toCommaSeparated(techLdap),
                acctgDt: toCommaSeparated(acctgDt),
                declineReason: toCommaSeparated(declineReason),
                applianceType: toCommaSeparated(applianceType),
                createSource: toCommaSeparated(createSource),
                llmChatClassification: toCommaSeparated(llmChatClassification),
                orderNumber: orderNumber || "",
                startDate,
                endDate,
                page,
                limit,
                sortColumn: sortColumn,
                sortDirection: sortDirection,
              }),
              signal,
            }
          );

          const feedData = await feedResponse.json();

          if (feedData.data) {
            setEdFeed(feedData.data);
            setTotalCount(feedData.pagination.total);
          } else {
            setEdFeed(feedData || []);
            setTotalCount(feedData?.length || 0);
          }

          const currentTime = new Date().toLocaleString();
          setLastUpdated(currentTime);
          if (onLastUpdatedChange) {
            onLastUpdatedChange(currentTime);
          }
        } catch (err: any) {
          // Don't show error if request was aborted
          if (err.name !== 'AbortError') {
            setError("Failed to fetch data");
            console.error("Error fetching data:", err);
            setEdFeed([]);
            setTotalCount(0);
            setLoading(false);
            setRefreshing(false);
          }
          // If aborted, keep loading state as is (don't set to false)
        } finally {
          // Only set loading to false if not aborted
          if (!signal?.aborted) {
            setLoading(false);
            setRefreshing(false);
          }
        }
      },
      [filterState, sortColumn, sortDirection, onLastUpdatedChange, toCommaSeparated]
    );

    useEffect(() => {
      const abortController = new AbortController();
      fetchData(false, abortController.signal);
      
      return () => {
        abortController.abort();
      };
    }, [filterState, sortColumn, sortDirection, fetchData]);

    useImperativeHandle(
      ref,
      () => ({
        refresh: () => fetchData(true),
        getLastUpdated: () => lastUpdated,
      }),
      [fetchData, lastUpdated]
    );

    // Column configurations for aggregated data by date
    const columnConfigs: ColumnConfig[] = [
      {
        name: "Decline Date",
        minWidth: "11ch",
        maxWidth: "11ch",
        width: "11ch",
        align: "left",
      },
      {
        name: "Total Completes",
        minWidth: "11ch",
        maxWidth: "11ch",
        width: "11ch",
        align: "center",
        rendererType: RendererType.NUMBER,
      },
      {
        name: "D2C Completes %",
        minWidth: "9ch",
        maxWidth: "9ch",
        width: "9ch",
        align: "center",
        rendererType: RendererType.PERCENTAGE,
      },
      {
        name: "Total D2C Completes",
        minWidth: "11ch",
        maxWidth: "11ch",
        width: "11ch",
        align: "center",
        rendererType: RendererType.NUMBER,
      },
      {
        name: "Estimate Declines",
        minWidth: "10ch",
        maxWidth: "10ch",
        width: "10ch",
        align: "center",
        rendererType: RendererType.NUMBER,
      },
      {
        name: "Estimate Decline %",
        minWidth: "10ch",
        maxWidth: "10ch",
        width: "10ch",
        align: "center",
        rendererType: RendererType.PERCENTAGE,
      },
      {
        name: "Total Quoted",
        minWidth: "12ch",
        maxWidth: "12ch",
        width: "12ch",
        align: "right",
        rendererType: RendererType.CURRENCY,
      },
      {
        name: "Total Quoted Per Call",
        minWidth: "9ch",
        maxWidth: "9ch",
        width: "9ch",
        align: "center",
        rendererType: RendererType.CURRENCY,
      },
      {
        name: "Labor Quoted",
        minWidth: "12ch",
        maxWidth: "12ch",
        width: "12ch",
        align: "right",
        rendererType: RendererType.CURRENCY,
      },
      {
        name: "Labor Quoted Per Call",
        minWidth: "9ch",
        maxWidth: "9ch",
        width: "9ch",
        align: "center",
        rendererType: RendererType.CURRENCY,
      },
      {
        name: "Parts Quoted",
        minWidth: "12ch",
        maxWidth: "12ch",
        width: "12ch",
        align: "right",
        rendererType: RendererType.CURRENCY,
      },
      {
        name: "Parts Quoted Per Call",
        minWidth: "9ch",
        maxWidth: "9ch",
        width: "9ch",
        align: "center",
        rendererType: RendererType.CURRENCY,
      },
      {
        name: "Parts Quoted Orders",
        minWidth: "9ch",
        maxWidth: "9ch",
        width: "9ch",
        align: "center",
        rendererType: RendererType.NUMBER,
      },
      {
        name: "Repair Higher Than MSRP",
        minWidth: "9ch",
        maxWidth: "9ch",
        width: "9ch",
        align: "center",
        rendererType: RendererType.NUMBER,
      },

      {
        name: "Repair Time LT 20 Mins",
        minWidth: "9ch",
        maxWidth: "9ch",
        width: "9ch",
        align: "center",
        rendererType: RendererType.NUMBER,
      },
      {
        name: "Repair Time GT 60 Mins",
        minWidth: "9ch",
        maxWidth: "9ch",
        width: "9ch",
        align: "center",
        rendererType: RendererType.NUMBER,
      },
      {
        name: "Deductible Not Collected",
        minWidth: "9ch",
        maxWidth: "9ch",
        width: "9ch",
        align: "center",
        rendererType: RendererType.NUMBER,
      },
    ];

    if (error) {
      return (
        <Box p={3}>
          <Alert severity="error">{error}</Alert>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          p: { xs: 2, md: 1.75 },
          bgcolor: "#f8fafc",
          minHeight: "100vh",
        }}
      >
        {/* Main Content */}
        <Grid container>
          <Grid item xs={12}>
            <Box sx={{ overflowX: "hidden" }}>
              {/* DataTable */}
              <DataTable
                data={edFeed}
                loading={loading || refreshing}
                maxHeight="600px"
                title={undefined}
                columnConfigs={columnConfigs}
                totalCount={totalCount}
                page={filterState.page}
                rowsPerPage={filterState.limit}
                onPageChange={(_, newPage) => {
                  onFilterChange(
                    true,
                    filterState.startDate,
                    filterState.endDate,
                    filterState.filter,
                    newPage,
                    filterState.limit
                  );
                }}
                onRowsPerPageChange={(event) => {
                  onFilterChange(
                    true,
                    filterState.startDate,
                    filterState.endDate,
                    filterState.filter,
                    0,
                    parseInt(event.target.value, 10)
                  );
                }}
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>
    );
  }
);

ByDate.displayName = "ByDate";

export default ByDate;
